import { useCallback, useEffect, useMemo, useState } from "react";
import type { Dog } from "../types";
import { extractDogs } from "../utils/extractDogs";

export function useDogsFromCustomer(customer: any) {
  // Extract initial dogs from customer JSON
  const initialDogs = useMemo(() => extractDogs(customer), [customer]);

  // Store dogs in local state so we can update them after API calls
  const [dogs, setDogs] = useState<Dog[]>(initialDogs);

  // Sync with customer prop when it changes (e.g., on page load)
  useEffect(() => {
    setDogs(initialDogs);
  }, [initialDogs]);

  const [activeDogId, setActiveDogId] = useState<string | null>(null);

  useEffect(() => {
    if (!activeDogId && dogs.length) setActiveDogId(dogs[0].id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dogs.length]);

  const activeDog: Dog | null = useMemo(() => {
    if (!dogs.length) return null;
    return dogs.find((d) => d.id === activeDogId) || dogs[0] || null;
  }, [dogs, activeDogId]);

  // Update a single dog in local state (for optimistic updates after API success)
  const updateLocalDog = useCallback((dogId: string, updates: Partial<Dog>) => {
    setDogs(prev => prev.map(dog =>
      dog.id === dogId
        ? { ...dog, ...updates }
        : dog
    ));
  }, []);

  return {
    dogs,
    setDogs,
    updateLocalDog,
    activeDogId,
    setActiveDogId,
    activeDog,
  };
}